"use client";

import type { ReactNode } from "react";

/**
 * Text with some spans marked — the display half of every "find X in your writing"
 * checker (nominalizations, hedges, repeated words, informal vocabulary…).
 *
 * The checker finds the spans; this draws them. Matches are character offsets into the
 * original text, so the reader sees their own words, untouched, with the hits marked in
 * place. Overlapping matches are resolved first-wins, which keeps the output a flat
 * sequence of runs rather than nested marks a screen reader would announce twice.
 */

export interface TextMatch {
  start: number;
  end: number;
  /** Key into `tones`, e.g. "hedge" or "booster". */
  category: string;
  /** Shown on hover/focus: the rule, or a suggested alternative. */
  title?: string;
}

/** Background/underline classes per category. Defaults suit a single category. */
export const HIGHLIGHT_TONES = {
  amber: "rounded bg-amber-200/70 px-0.5 text-foreground dark:bg-amber-400/30",
  sky: "rounded bg-sky-200/70 px-0.5 text-foreground dark:bg-sky-400/30",
  rose: "rounded bg-rose-200/70 px-0.5 text-foreground dark:bg-rose-400/30",
  emerald:
    "rounded bg-emerald-200/70 px-0.5 text-foreground dark:bg-emerald-400/30",
  violet:
    "rounded bg-violet-200/70 px-0.5 text-foreground dark:bg-violet-400/30",
} as const;

export type HighlightTone = keyof typeof HIGHLIGHT_TONES;

function flatten(matches: readonly TextMatch[], length: number): TextMatch[] {
  const ordered = [...matches]
    .filter((m) => m.end > m.start && m.start >= 0 && m.end <= length)
    .sort((a, b) => a.start - b.start || b.end - a.end);
  const flat: TextMatch[] = [];
  let cursor = 0;
  for (const match of ordered) {
    if (match.start < cursor) {
      continue;
    }
    flat.push(match);
    cursor = match.end;
  }
  return flat;
}

export function HighlightedText({
  text,
  matches,
  tones,
  label,
}: {
  text: string;
  matches: readonly TextMatch[];
  /** Category → tone. Unknown categories use amber. */
  tones?: Record<string, HighlightTone>;
  /** Accessible name for the region, e.g. "Your text with hedges highlighted". */
  label: string;
}) {
  const runs: ReactNode[] = [];
  let cursor = 0;
  for (const match of flatten(matches, text.length)) {
    if (match.start > cursor) {
      runs.push(text.slice(cursor, match.start));
    }
    const tone = tones?.[match.category] ?? "amber";
    runs.push(
      <mark
        className={HIGHLIGHT_TONES[tone]}
        key={`${match.start}-${match.end}`}
        title={match.title}
      >
        {text.slice(match.start, match.end)}
      </mark>
    );
    cursor = match.end;
  }
  if (cursor < text.length) {
    runs.push(text.slice(cursor));
  }
  return (
    <section
      aria-label={label}
      className="max-h-[480px] overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed"
    >
      {runs}
    </section>
  );
}
